// src/components/smart_home_builder/FunctionSelector.jsx
import React from "react";
import { motion } from "framer-motion";
import { useNavigate } from "react-router-dom";

// Ugyanazok a motion-variánsok, mint a SmartHomeFunctionsPage-ben
const fadeInUp = {
  hidden: { opacity: 0, y: 14 },
  show: { opacity: 1, y: 0, transition: { duration: 0.35, ease: "easeOut" } },
};

const staggerContainer = (delay = 0, stagger = 0.06) => ({
  hidden: {},
  show: { transition: { delay, staggerChildren: stagger, when: "beforeChildren" } },
});

const cardVariant = {
  hidden: { opacity: 0, y: 10, scale: 0.98 },
  show: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.28 } },
};

export default function FunctionSelector({
  functionList,
  selectedFunctions,
  onToggle,
  onNext,
}) {
  const navigate = useNavigate();

  const handleCardKeyDown = (e, id) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      onToggle(id);
    }
  };

  return (
    <>
      <motion.header
        className="mb-6"
        initial="hidden"
        animate="show"
        variants={staggerContainer(0.02, 0.06)}
      >
        <motion.h2 className="text-2xl md:text-3xl font-bold" variants={fadeInUp}>
          Válaszd ki a funkciókat
        </motion.h2>
        <motion.p className="text-gray-700 mt-1" variants={fadeInUp}>
          Kattints a kártyákra a kiválasztáshoz. A „Részletek” gomb az adott funkció magyarázó oldalára visz.
        </motion.p>
      </motion.header>

      <motion.section
        className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-5"
        initial="hidden"
        animate="show"
        variants={staggerContainer(0.05, 0.08)}
      >
        {functionList.map((f, idx) => {
          const active =
            Array.isArray(selectedFunctions) && selectedFunctions.includes(f.id);
          const imgSrc = `/img/base/${f.id}.webp`; // egységes képfájl-konvenció

          return (
            <motion.article
              key={f.id}
              role="button"
              tabIndex={0}
              aria-pressed={active}
              onKeyDown={(e) => handleCardKeyDown(e, f.id)}
              onClick={(e) => {
                onToggle(f.id);
                requestAnimationFrame(() => {
                  try {
                    e.currentTarget.blur();
                  } catch {}
                });
              }}
              className={`group text-left bg-white rounded-2xl border ${
                active ? "border-blue-500 ring-2 ring-blue-200" : "border-gray-200"
              } hover:border-gray-300 shadow-sm hover:shadow-md transition-all focus:outline-none`}
              variants={cardVariant}
              whileHover={{ y: -4, boxShadow: "0 10px 24px rgba(12,26,38,0.10)" }}
            >
              {/* Kép (ugyanaz az arány, animáció, mint a SmartHomeFunctionsPage-en) */}
              <div className="p-4">
                <div className="aspect-[4/3] w-full overflow-hidden rounded-xl mb-4 bg-gray-50">
                  <motion.img
                    src={imgSrc}
                    alt={f.title}
                    className="w-full h-full object-contain"
                    loading="lazy"
                    onError={(e) => {
                      e.currentTarget.src = "/img/placeholder.webp";
                    }}
                    initial={{ scale: 1.02, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ duration: 0.4, ease: "easeOut", delay: 0.02 * idx }}
                    whileHover={{ scale: 1.04 }}
                  />
                </div>
              </div>

              {/* Szöveg + akciósor */}
              <div className="px-4 pb-4">
                <div className="flex items-start gap-2">
                  <h3 className="text-lg font-semibold flex-1">{f.title}</h3>
                  {active && (
                    <span className="px-2 py-0.5 rounded bg-blue-50 border border-blue-200 text-[11px] text-blue-700">
                      Kiválasztva
                    </span>
                  )}
                </div>

                {f.desc && (
                  <p className="text-sm text-gray-600 mt-1 mb-3 line-clamp-3">{f.desc}</p>
                )}

                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation(); // ne toggolja a kártyát
                      navigate(`/funkciok/${f.id}`);
                    }}
                    className="px-3 py-1.5 rounded-lg border text-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 ml-auto"
                    aria-label={`${f.title} – részletek`}
                  >
                    Részletek
                  </button>
                </div>

                <p className="text-[11px] text-gray-500 mt-3">
                  Tipp: kattints a kártyára a kiválasztáshoz vagy a kiválasztás
                  megszüntetéséhez. A „Részletek” külön oldalon magyarázza el a funkciót.
                </p>
              </div>
            </motion.article>
          );
        })}
      </motion.section>

      <div className="flex justify-between mt-6">
        <span className="text-sm text-gray-500">
          Kiválasztott funkciók: {selectedFunctions?.length || 0}
        </span>
        <button
          className="bg-blue-600 text-white px-5 py-2 rounded hover:bg-blue-700 disabled:opacity-50"
          disabled={!selectedFunctions || selectedFunctions.length === 0}
          onClick={onNext}
          type="button"
        >
          Tovább a termékválasztáshoz
        </button>
      </div>
    </>
  );
}
